// src/components/FlowingReviewsTicker.jsx
import React, { useState, useEffect } from 'react';
import { Star, Quote } from 'lucide-react';
import reviewService from '../services/reviewService';
import LogamiLogoSpinner from './ui/LogamiLogoSpinner';

const FlowingReviewsTicker = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      // Use your existing reviewService which combines fake + real reviews
      const reviewsData = await reviewService.getReviews({
        limitCount: 30, // Get all reviews
        sortBy: 'newest'
      });

      setReviews(reviewsData);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  // Render stars
  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[...Array(5)].map((_, index) => (
          <Star
            key={index}
            className={`w-3 h-3 ${
              index < rating ? 'fill-black text-black' : 'fill-gray-300 text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  // Compact Review Card
  const ReviewCard = ({ review }) => (
    <div className="review-card-ticker flex-shrink-0 w-80 mx-3 bg-white border-2 border-black/10 rounded-xl p-4 shadow-lg hover:shadow-2xl hover:border-black/30 cursor-pointer">
      {/* Quote icon */}
      <Quote className="w-6 h-6 text-black/20 mb-2" />

      {/* Review text */}
      <p className="text-sm text-black/80 mb-3 line-clamp-3 leading-relaxed min-h-[60px]">
        "{review.comment}"
      </p>

      {/* Footer with avatar, name, and rating */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Avatar */}
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-black to-gray-600 flex items-center justify-center text-white font-bold text-xs">
            {review.userInitials || review.name?.charAt(0).toUpperCase() || 'A'}
          </div>

          {/* Name and exam */}
          <div>
            <div className="font-bold text-xs text-black">
              {review.userName || review.name || 'Anonymous'}
            </div>
            <div className="text-xs text-black/50">
              {review.exam || 'B2 · Goethe'}
            </div>
          </div>
        </div>

        {/* Stars */}
        <div>
          {renderStars(review.rating)}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="py-8 text-center">
        <LogamiLogoSpinner size="lg" className="inline-block" decorative={false} alt="Logami loading" />
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-black/60">No reviews yet. Be the first to share your experience!</p>
      </div>
    );
  }

  // Split reviews into 3 rows for visual variety
  const row1Reviews = reviews.slice(0, Math.ceil(reviews.length / 3));

  // Duplicate reviews for seamless infinite scroll
  const duplicateReviews = (reviewsArray) => [...reviewsArray, ...reviewsArray, ...reviewsArray];

  return (
    <div className="space-y-4 overflow-hidden">
      {/* Row 1 - Scrolling Right to Left */}
      <div className="relative overflow-hidden">
        <div className="scroll-container scroll-left" style={{ animationDuration: '40s' }}>
          {duplicateReviews(row1Reviews).map((review, index) => (
            <ReviewCard key={`row1-${review.id}-${index}`} review={review} />
          ))}
        </div>

        {/* Fade edges */}
        <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-gray-50 to-transparent pointer-events-none z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-gray-50 to-transparent pointer-events-none z-10" />
      </div>

    </div>
  );
};

export default FlowingReviewsTicker;